import {
  buildCartKey,
  buildCreateOrderPayload,
  buildTableOrderPayload,
  buildProductsIndex,
  calcCartItemUnitPrice,
  getOrderPaymentResourceId,
  mapFastOrderResponseToCheckDetails,
  setCartProduct,
  setCartProductFromStorage,
  setCheckDetails,
  setQrMenuPallete
} from '@/composables/qr-menu/model'
import { useQRMenuRequests } from '@/composables/qr-menu/requests'
import { checkProductAvailability } from '@/composables/client/requests'
import { checkRestaurantStatus } from '@/composables/client/requests/checkRestaurantStatus'
import {
  AvailableLangs,
  CartProduct,
  CartProductFromStorage,
  Pallete,
  ProductAvailabilityData,
  QrMenuCategory,
  QrMenuCheckDetails,
  QrMenuFastOrderPayment,
  QrMenuFastOrderResponse,
  QrMenuHeader,
  QrMenuOrderType,
  QrMenuProduct
} from '@/composables/qr-menu/types'
import type { ApiError } from '@/api/types'
import { useHead } from '@/composables/shared/useHead'
import { Story } from '@/composables/stories/types'
import { getCurrentLocale } from '@/plugins/i18n/models'
import { useLocalStorage, useSessionStorage } from '@vueuse/core'
import { useConfirm } from 'primevue'
import { defineStore, storeToRefs } from 'pinia'
import { AxiosError } from 'axios'
import { computed, nextTick, reactive, ref, watch } from 'vue'
import { useRoute, useRouter, type RouteLocationRaw } from 'vue-router'
import { useQrMenuPaymentStore } from '@/store/qr-menu-payment'
import { useAppToast } from '@/utils/toast'
import { i18n } from '@/plugins/i18n'

export const useQrMenuStore = defineStore('qr-menu', () => {
  const router = useRouter()
  const route = useRoute()
  const {
    getList,
    createOrder,
    createTableOrder,
    getCreatedOrder,
    confirmOrderPayment,
    resendOrderPaymentOtp: requestResendOrderPaymentOtp
  } = useQRMenuRequests()
  const { setBodyColor } = useHead()
  const { showErrorToast, showSuccessToast } = useAppToast()

  const paymentStore = useQrMenuPaymentStore()
  const { paymentSystemList } = storeToRefs(paymentStore)

  /** Экран заказа после оформления: ошибка, uuid маршрута, откуда вернуться в меню */
  const orderFlow = reactive({
    error: null as string | null,
    uuid: null as string | null,
    menuReturnLocation: null as RouteLocationRaw | null,
    sourceRouteName: null as string | null
  })

  /**
   * Состояние после POST fast-order: блок payment, редирект-диалог (демо), OTP оплаты картой.
   */
  const fastOrderPay = reactive({
    details: null as QrMenuFastOrderPayment | null,
    redirectDialogVisible: false,
    otpModal: {
      visible: false,
      code: '',
      loading: false
    }
  })

  const menuCheckout = paymentStore.menuCheckout

  const menuLoadError = ref<string | null>(null)

  /** null = статус ещё не получен, true = доступен, false = недоступен */
  const restaurantAvailable = ref<boolean | null>(null)
  const restaurantStatusLoading = ref(false)

  // Если ресторан недоступен — очищаем корзину
  watch(restaurantAvailable, available => {
    if (available === false) {
      cart.value = {}
      currentCartId.value = ''
      cartOrderType.value = 'spot'
      cartOrderComment.value = ''
      paymentStore.resetPaymentForm()
    }
  })

  const menuInitialized = ref(false)
  const headerData = ref<QrMenuHeader>()
  const stories = ref<Story[]>([])
  const menuList = ref<QrMenuCategory[]>([])
  const checkDetails = ref<QrMenuCheckDetails | null>(null)
  const productDetailVisible = ref(false)
  const selectedProduct = ref<QrMenuProduct>()
  /** Актуальные данные о доступности выбранного продукта (из API /availability) */
  const selectedProductAvailability = ref<ProductAvailabilityData | null>(null)
  const loading = ref(false)
  const orderLoading = ref(false)
  const menuLang = ref(getCurrentLocale())
  const themeResponse = ref<Partial<Pallete>>(setQrMenuPallete())
  const cart = useLocalStorage<Record<string, CartProduct>>('cart', {})
  const currentTableNumber = useLocalStorage<string>('currentTableNumber', '')
  const currentCartId = useLocalStorage<string>('currentCartId', '')

  /**
   * Сессия входа в меню: фиксируем brandId и ключевые query-параметры (t, f, s, d)
   * при первом входе на страницу меню. Если параметры изменились — сбрасываем и пересохраняем.
   * Используется для построения надёжного back_url на страницах корзины и заказа.
   *
   * sessionStorage (не localStorage): изолирован per-tab — две открытые вкладки с разными
   * ресторанами не перезаписывают сессию друг друга. Переживает перезагрузку страницы.
   *
   * Явный JSON-сериализатор обязателен: с default=null VueUse использует 'any'-сериализатор
   * (String(value)) → объект превращается в "[object Object]".
   */
  const menuSession = useSessionStorage<{
    brandId: string
    query: Record<string, string>
  } | null>('menuSession', null, {
    serializer: {
      read: (v: string) => {
        try {
          return JSON.parse(v) as { brandId: string; query: Record<string, string> }
        } catch {
          return null
        }
      },
      write: (v) => JSON.stringify(v)
    }
  })

  /** Готовый back_url в меню, вычисленный из зафиксированной сессии */
  const menuBackUrl = computed((): RouteLocationRaw | null => {
    const session = menuSession.value
    if (!session) return null
    const q = session.query
    return Object.keys(q).length > 0
      ? { path: `/m/${session.brandId}`, query: q }
      : { path: `/m/${session.brandId}` }
  })
  const cartOrderType = ref<QrMenuOrderType>('spot')
  const cartOrderComment = ref('')
  const availableLocales = ref<null | AvailableLangs>(null)
  const isCartVisible = computed(
    () =>
      (route.params.id && currentCartId.value === (route.params.id as string)) ||
      currentTableNumber.value === (route.query.table as string)
  )
  const selectedModifiersByProduct = ref<Record<string, Array<{ id: number; amount: number }>>>({})
  const $confirm = useConfirm()

  const addToCart = (
    product: QrMenuProduct,
    delta: number = 1,
    options?: { keepProductDetailOpen?: boolean }
  ) => {
    if (restaurantAvailable.value === false) return

    const closeDetailAfter = options?.keepProductDetailOpen !== true
    const mods = selectedModifiersByProduct.value[product.id] ?? []
    const key = buildCartKey(product, mods)
    const prev = cart.value[key]
    const next = setCartProduct(product, mods, prev, delta)

    const routeRestaurantId = route.params.id as string
    const routeTableNumber = (route.query.table as string) || ''

    if (!currentCartId.value) {
      currentCartId.value = routeRestaurantId
    } else {
      const tableChanged =
        Boolean(currentTableNumber.value) &&
        Boolean(routeTableNumber) &&
        currentTableNumber.value !== routeTableNumber

      if (currentCartId.value !== routeRestaurantId || tableChanged) {
        $confirm.require({
          group: 'client',
          message: i18n.global.t('qrMenu.confirmClearCart.message'),
          header: i18n.global.t('qrMenu.confirmClearCart.header'),
          rejectProps: {
            label: i18n.global.t('qrMenu.confirmClearCart.cancel'),
            severity: 'secondary',
            outlined: true
          },
          acceptProps: {
            label: i18n.global.t('qrMenu.confirmClearCart.accept'),
            severity: 'danger'
          },
          accept() {
            currentCartId.value = routeRestaurantId
            currentTableNumber.value = routeTableNumber
            cart.value = {}
            cart.value[key] = next
            if (closeDetailAfter) {
              productDetailVisible.value = false
            }
          }
        })
        return
      }
    }

    if (!currentTableNumber.value && routeTableNumber) {
      currentTableNumber.value = routeTableNumber
    }

    if (next.count <= 0) {
      delete cart.value[key]
    } else {
      cart.value[key] = next
    }

    if (closeDetailAfter) {
      productDetailVisible.value = false
    }
  }

  const removeFromCart = (key: string) => {
    delete cart.value[key]
    if (Object.keys(cart.value).length === 0) {
      router.back()
    }
  }

  const resetCart = () => {
    cart.value = {}
    currentCartId.value = ''
    cartOrderType.value = 'spot'
    cartOrderComment.value = ''
    paymentStore.resetPaymentForm()
    router.back()
  }

  const clearCartAfterSuccessfulOrderPayment = () => {
    cart.value = {}
    selectedModifiersByProduct.value = {}
    cartOrderComment.value = ''
    currentCartId.value = ''
    cartOrderType.value = 'spot'
    paymentStore.resetPaymentForm()
  }

  const updateCartProductCount = (key: string, count: number) => {
    cart.value[key].count = count
  }

  const updateSelectedModifiers = (
    product: QrMenuProduct,
    mods: Array<{ id: number; amount: number }>
  ) => {
    selectedModifiersByProduct.value[product.id] = mods
  }

  /**
   * Строим back URL из текущего route — вызывается в момент перехода на order,
   * когда id и query ещё актуальны (до смены роута).
   */
  const buildOrderScreenMenuReturnLocation = (): RouteLocationRaw | null => {
    const id = route.params.id as string | undefined
    if (!id) return null
    const q: Record<string, string> = {}
    for (const key of MENU_SESSION_KEYS) {
      const v = route.query[key]
      if (v && typeof v === 'string') q[key] = v
    }
    return Object.keys(q).length > 0 ? { path: `/m/${id}`, query: q } : { path: `/m/${id}` }
  }

  const redirectToOrderScreen = async () => {
    paymentStore.clearPendingCardTokenForOrder()
    const backLocation = buildOrderScreenMenuReturnLocation()
    orderFlow.menuReturnLocation = backLocation
    const name = route.name
    orderFlow.sourceRouteName =
      typeof name === 'string' ? name : name != null ? String(name) : null
    const uuid = orderFlow.uuid

    // Встраиваем back URL прямо в query параметр — так он выживает после
    // перезагрузки и не зависит от storage или состояния других вкладок
    const query: Record<string, string> = {}
    if (backLocation) {
      query.back = encodeURIComponent(
        typeof backLocation === 'string' ? backLocation : (backLocation as { path: string; query?: Record<string, string> }).path +
          (Object.keys((backLocation as { query?: Record<string, string> }).query ?? {}).length
            ? '?' + new URLSearchParams((backLocation as { query: Record<string, string> }).query).toString()
            : '')
      )
    }
    await router.push({ name: 'qr-menu-order', params: { uuid }, query })
  }

  const finishPaymentReviewAndGoToOrder = async (errorMessage: string | null) => {
    fastOrderPay.redirectDialogVisible = false
    fastOrderPay.details = null
    if (errorMessage == null) {
      clearCartAfterSuccessfulOrderPayment()
    }
    orderFlow.error = errorMessage
    await redirectToOrderScreen()
  }

  const closeOrderPaymentOtpModal = () => {
    fastOrderPay.otpModal.visible = false
    fastOrderPay.otpModal.code = ''
  }

  const resendOrderPaymentOtp = async (): Promise<boolean> => {

    const captcha = await window.grecaptcha.execute('6LdSo3krAAAAAHIFJQO-0iveC6pQTrJc5B6mRKvT', { action: 'resend_order_payment_otp' })

    const p = fastOrderPay.details
    if (!p) return false
    const pid = getOrderPaymentResourceId(p)
    if (!pid) return false
    const cardToken = (
      (menuCheckout.pendingCardToken ?? paymentStore.selectedCard ?? '') as string
    ).trim()
    if (!cardToken) {
      showErrorToast(
        i18n.global.t('toast.error'),
        i18n.global.t('qrMenu.orderPaymentOtp.missingCardToken')
      )
      return false
    }
    try {
      const body = await requestResendOrderPaymentOtp(pid, cardToken, captcha)
      if (!body?.success) {
        const b = body as { error?: { details?: string; message?: string } } | null
        showErrorToast(
          i18n.global.t('toast.error'),
          b?.error?.details ||
          b?.error?.message ||
          i18n.global.t('qrMenu.orderPaymentOtp.resendFailed')
        )
        return false
      }
      // Бэкенд возвращает новый объект payment с новым id — обновляем fastOrderPay.details,
      // чтобы submitOrderPaymentOtp использовал актуальный pid через getOrderPaymentResourceId
      const newPayment = body.data?.payment
      if (newPayment) {
        fastOrderPay.details = newPayment
      }
      showSuccessToast(
        i18n.global.t('toast.success'),
        i18n.global.t('qrMenu.orderPaymentOtp.resendSent')
      )
      return true
    } catch (error) {
      const err = error as AxiosError<ApiError>
      showErrorToast(
        i18n.global.t('toast.error'),
        err.response?.data?.error?.details ||
        err.response?.data?.error?.message ||
        i18n.global.t('qrMenu.orderPaymentOtp.resendFailed')
      )
      return false
    }
  }

  const submitOrderPaymentOtp = async () => {
    const digits = fastOrderPay.otpModal.code.replace(/\D/g, '')
    if (digits.length !== 6) return
    const p = fastOrderPay.details
    if (!p) return
    const pid = getOrderPaymentResourceId(p)
    if (!pid) return
    fastOrderPay.otpModal.loading = true
    try {
      const body = await confirmOrderPayment(pid, Number(digits))
      if (!body?.success) {
        const b = body as { error?: { details?: string; message?: string } } | null
        showErrorToast(
          i18n.global.t('toast.error'),
          b?.error?.details || b?.error?.message || i18n.global.t('qrMenu.confirmPaymentFailed')
        )
        return
      }
      clearCartAfterSuccessfulOrderPayment()
      closeOrderPaymentOtpModal()
      fastOrderPay.details = null
      await redirectToOrderScreen()
    } catch (error) {
      const err = error as AxiosError<ApiError>
      showErrorToast(
        i18n.global.t('toast.error'),
        err.response?.data?.error?.details ||
        err.response?.data?.error?.message ||
        i18n.global.t('qrMenu.confirmPaymentOtpFailed')
      )
    } finally {
      fastOrderPay.otpModal.loading = false
    }
  }

  /**
   * Сценарий оформления из клиентского меню: `t` в query — заказ за столом (POST `/menu/table-order`),
   * иначе — быстрый чек / spot (POST `/menu/fast-order`, в т.ч. `?f=store_id`).
   */
  const clientMenuCheckoutFlow = computed(() => {
    const t = String(route.query.t ?? '').trim()
    return t ? ('table' as const) : ('fast' as const)
  })

  const getCheckDetails = async () => {
    if (restaurantAvailable.value === false) return

    orderFlow.error = null
    fastOrderPay.details = null
    orderFlow.uuid = null
    fastOrderPay.otpModal.visible = false
    fastOrderPay.otpModal.code = ''
    orderLoading.value = true
    try {
      const hadCardToken = Boolean((menuCheckout.pendingCardToken ?? '').trim())
      let res: QrMenuFastOrderResponse
      if (clientMenuCheckoutFlow.value === 'table') {
        const tableId = String(route.query.t ?? '').trim()
        if (!tableId) {
          showErrorToast(i18n.global.t('toast.error'), i18n.global.t('qrMenu.tableOrderMissingTableId'))
          return
        }
        res = await createTableOrder(createTableOrderPayload.value)
      } else {
        res = await createOrder(createOrderPayload.value)
      }
      const normalized = setCheckDetails(mapFastOrderResponseToCheckDetails(res.data))
      checkDetails.value = normalized
      const id = normalized.uuid ?? normalized.order_id ?? normalized.uniqid ?? null
      orderFlow.uuid =
        id != null && String(id).trim() !== '' ? String(id).trim() : null
      fastOrderPay.details = res.data.payment ?? null
      const payment = res.data.payment
      if (payment) {
        if (hadCardToken) {
          fastOrderPay.redirectDialogVisible = false
          const payId = getOrderPaymentResourceId(payment)
          if (payId) {
            fastOrderPay.otpModal.visible = true
          } else {
            await redirectToOrderScreen()
          }
        } else {
          fastOrderPay.redirectDialogVisible = true
          const redirectUrl = payment.redirect?.trim()
          if (redirectUrl) {
            window.location.assign(redirectUrl)
          }
        }
      } else {
        fastOrderPay.redirectDialogVisible = false
        await redirectToOrderScreen()
      }
    } catch (error) {
      checkDetails.value = null
      fastOrderPay.details = null
      const err = error as AxiosError<ApiError>
      const msg =
        err.response?.data?.error?.details ||
        err.response?.data?.error?.message ||
        i18n.global.t('qrMenu.orderPlaceFailedTitle')
      orderFlow.error = msg
      orderFlow.uuid = 'error'
      await redirectToOrderScreen()
    } finally {
      orderLoading.value = false
    }
    // uuid остаётся null после сброса в начале метода → router.push с null-param
    // тихо падает и Order.vue никогда не рендерится.
    // Плейсхолдер 'error' позволяет роуту совпасть, а useQrMenuOrderScreen
    // пропустит fetch для этого значения и отдаст управление orderFlow.error.
  }

  const clearOrderScreenContext = () => {
    orderFlow.error = null
    orderFlow.uuid = null
    orderFlow.menuReturnLocation = null
    orderFlow.sourceRouteName = null
  }

  const clearOrderScreenError = () => {
    orderFlow.error = null
  }

  const navigateToMenuFromOrderScreen = async () => {
    const loc = orderFlow.menuReturnLocation
    try {
      await router.push(loc ?? { name: 'stores' })
    } finally {
      clearOrderScreenContext()
    }
  }


  const selectedCategoryId = computed<number | null>({
    get() {
      const value = route.query.category
      return value ? Number(value) : null
    },
    set(id) {
      searchQuery.value = ''
      router.push({ query: { ...route.query, category: id } }).then(() => {
        window.scrollTo({
          top: 0
        })
      })
    }
  })

  const selectedParentId = computed<number | 'other' | null>({
    get() {
      const value = route.query.parent
      if (value === 'other') return 'other'
      if (value) return Number(value)

      if (!noChildFolders.value) {
        if (parentFolders.value.length > 0) {
          return parentFolders.value[0].id
        }
        if (otherFolders.value.length > 0) {
          return 'other'
        }
      }
      return null
    },
    set(id) {
      router.push({ query: { ...route.query, parent: id, category: undefined } }).then(() => {
        window.scrollTo({
          top: 0
        })
      })
    }
  })

  const themeVars = computed(() => {
    return {
      '--bg-color': themeResponse.value.bgColor,
      '--card-color': themeResponse.value.productCardColor,
      '--primary-color': themeResponse.value.primaryColor,
      '--secondary-color': themeResponse.value.secondaryColor,
      '--text-color': themeResponse.value.textColor,
      '--border-color': themeResponse.value.borderColor,
      '--secondary-text-color': themeResponse.value.secondaryTextColor,
      '--primary-500': themeResponse.value.primaryColor
    }
  })

  const parentFolders = computed(() => {
    return menuList.value.filter(category => !category.parentId && !category.items.length)
  })

  const parentFoldersIdEnum = computed<Record<number, number>>(() => {
    return parentFolders.value.reduce(
      (acc, folder) => {
        acc[folder.id] = folder.id
        return acc
      },
      {} as Record<number, number>
    )
  })

  const otherFolders = computed(() => {
    return menuList.value.filter(
      folder =>
        !parentFoldersIdEnum.value[folder.id] &&
        (!folder.parentId || !parentFoldersIdEnum.value[folder.parentId])
    )
  })

  const selectedCategory = computed(
    () => menuList.value.find(cat => cat.id === selectedCategoryId.value) || null
  )

  const noChildFolders = computed(() => {
    return !menuList.value.some(cat => cat.parentId)
  })

  const childCategories = computed(() => {
    if (selectedParentId.value === 'other') {
      return otherFolders.value
    }
    if (typeof selectedParentId.value === 'number') {
      return menuList.value.filter(cat => cat.parentId === selectedParentId.value)
    }
    return []
  })

  const clearMenuLoadError = () => {
    menuLoadError.value = null
  }

  const MENU_SESSION_KEYS = ['t', 'f', 's', 'd'] as const

  const captureMenuSession = (brandId: string) => {
    const query: Record<string, string> = {}
    for (const key of MENU_SESSION_KEYS) {
      const v = route.query[key]
      if (v && typeof v === 'string') query[key] = v
    }

    const current = menuSession.value
    const brandChanged = current?.brandId !== brandId
    const queryChanged = JSON.stringify(current?.query ?? {}) !== JSON.stringify(query)

    if (brandChanged || queryChanged) {
      // Сессия изменилась — сбрасываем корзину, связанное состояние и последний заказ
      cart.value = {}
      currentCartId.value = ''
      currentTableNumber.value = ''
      selectedModifiersByProduct.value = {}
      cartOrderComment.value = ''
      cartOrderType.value = 'spot'
      paymentStore.resetPaymentForm()

      menuSession.value = { brandId, query }
    }
  }

  const getQrMenu = async () => {
    if (route.name !== 'qr-menu' && route.name !== 'cart') {
      return
    }
    const brandId = route.params.id as string | undefined
    if (!brandId) {
      return
    }

    menuLoadError.value = null
    loading.value = true
    try {
      const res = await getList({
        brandId,
        storeId: route.query.s as string,
        tableId: route.query.t as string,
        delivery: route.query.d as string,
        fast: route.query.f as string,
        locale: menuLang.value
      })
      availableLocales.value = res?.data?.available_locales

      headerData.value = {
        id: res.data?.id,
        name: res.data?.name,
        address: res.data?.address,
        lat: res.data?.lat,
        lng: res.data?.lng,
        logo: res.data?.logo,
        background_url: res.data?.background_url,
        wifi: res.data?.wifi,
        phone: res.data?.phone,
        instagram: res.data?.instagram,
        menu_id: res.data?.menu_id,
        restaurant_id: res.data?.restaurant_id,
        store_id: res.data?.store_id,
        close: res.data?.close,
        open: res.data?.open,
        facebook: res.data?.facebook,
        telegram: res.data?.telegram,
        avg_bill: res.data?.avg_bill,
        avg_cooking: res.data?.avg_cooking,
        rating: res.data?.rating,
        cashback: res.data?.cashback
      }
      themeResponse.value = setQrMenuPallete({
        theme: res.data?.theme,
        primaryColor: res.data?.color
      })
      menuList.value = res.data?.result.sort((a, b) => a.order - b.order)
      stories.value = res.data?.stories.sort((a, b) => a.order - b.order)

      captureMenuSession(brandId)

      if (themeResponse.value.bgColor) setBodyColor(themeResponse.value.bgColor)

      // if (!noChildFolders.value) {
      //   if (!('parent' in route.query)) {
      //     if (parentFolders.value.length) {
      //       router.push({ query: { ...route.query, parent: parentFolders.value[0].id } })
      //     } else {
      //       router.push({ query: { ...route.query, parent: 'other' } })
      //     }
      //   }
      // }
      await paymentStore.fetchPaymentSystems()
      await nextTick()
    } catch (error) {
      const msg = error instanceof Error ? error.message : i18n.global.t('qrMenu.menuLoadErrorTitle')
      menuLoadError.value = msg
      console.error(error)
    } finally {
      setTimeout(() => {
        loading.value = false
      }, 150)
      menuInitialized.value = true
    }
  }

  const searchQuery = ref('')

  const flatProductList = computed(() => {
    return menuList.value.flatMap(category =>
      category.items.map(product => ({
        ...product,
        categoryId: category.id,
        categoryName: category.name
      }))
    )
  })

  const productsIndex = computed(() =>
    buildProductsIndex(flatProductList.value as unknown as QrMenuProduct[])
  )

  const searchedProducts = computed(() => {
    const query = searchQuery.value.trim().toLowerCase()
    if (!query) return []

    return flatProductList.value.filter(product => product.name.toLowerCase().includes(query))
  })

  const cartTotalPrice = computed(() => {
    const byId = productsIndex.value.byId
    return Object.values(cart.value).reduce((total, cartItem) => {
      const product = byId.get(cartItem.id)
      const singleItemPrice = calcCartItemUnitPrice(product, cartItem)
      return total + singleItemPrice * (cartItem.count ?? 0)
    }, 0)
  })


  const createOrderPayload = computed(() => {
    const cardToken = (menuCheckout.pendingCardToken ?? '').trim()
    const payByCard = cardToken.length > 0
    return buildCreateOrderPayload(
      headerData.value?.menu_id as string,
      headerData.value?.store_id as unknown as string,
      cartProductsForOrder.value,
      flatProductList.value,
      cartTotalPrice.value,
      cartOrderType.value,
      cartOrderComment.value || undefined,
      payByCard ? undefined : menuCheckout.paymentSystem,
      payByCard ? cardToken : null,
    )
  })

  const createTableOrderPayload = computed(() => {
    const cardToken = (menuCheckout.pendingCardToken ?? '').trim()
    const payByCard = cardToken.length > 0
    const tableId = String(route.query.t ?? '').trim()
    return buildTableOrderPayload(
      headerData.value?.menu_id as string,
      tableId,
      cartProductsForOrder.value,
      flatProductList.value,
      cartTotalPrice.value,
      cartOrderComment.value || undefined,
      payByCard ? undefined : menuCheckout.paymentSystem,
      payByCard ? cardToken : null
    )
  })

  const paymentSystem = computed(() => menuCheckout.paymentSystem)

  const cartItemsCount = computed(() => {
    return Object.keys(cart.value).length
  })

  const cartProductsForOrder = computed<CartProductFromStorage[]>(() => {
    const byId = productsIndex.value.byId
    const nameById = productsIndex.value.modifierNameById

    return Object.values(cart.value)
      .map(cartItem => {
        const product = byId.get(cartItem.id)
        if (!product) return null

        const mappedModifiers = (cartItem.modifiers ?? []).map(m => ({
          id: m.id,
          amount: m.amount ?? 0,
          name: nameById.get(m.id) ?? ''
        }))

        const unitPrice = calcCartItemUnitPrice(product, cartItem)

        const orderItem: CartProductFromStorage = setCartProductFromStorage(
          product,
          mappedModifiers,
          {
            key: cartItem.key,
            count: cartItem.count ?? 0,
            price: unitPrice
          }
        )

        return orderItem
      })
      .filter((x): x is CartProductFromStorage => Boolean(x))
  })

  const setSelectedProduct = (product: QrMenuProduct) => {
    selectedProduct.value = product
    // Сбрасываем старую доступность — свежая придёт через setSelectedProductAvailability
    selectedProductAvailability.value = null
    productDetailVisible.value = true
  }

  const setSelectedProductAvailability = (availability: ProductAvailabilityData | null) => {
    selectedProductAvailability.value = availability
  }

  /**
   * Открывает детали продукта немедленно, а в фоне запрашивает доступность.
  /**
   * Проверяет доступность ресторана и обновляет restaurantAvailable.
   * Повторный вызов игнорируется, если уже выполняется запрос.
   */
  const checkRestaurantAvailability = async (): Promise<void> => {
    const restaurantId = headerData.value?.restaurant_id
    const menuId = headerData.value?.menu_id
    if (!restaurantId || !menuId || restaurantStatusLoading.value) return

    restaurantStatusLoading.value = true
    try {
      restaurantAvailable.value = await checkRestaurantStatus(restaurantId, menuId)
    } catch {
      restaurantAvailable.value = false
    } finally {
      restaurantStatusLoading.value = false
    }
  }

  /**
   * Используется при клике на карточку продукта (не на кнопку +).
   */
  const openProductDetail = (product: QrMenuProduct) => {
    setSelectedProduct(product)
    const storeId = headerData.value?.store_id
    const menuId = headerData.value?.menu_id
    if (!storeId || !menuId) return
    void checkProductAvailability(product.product_id, storeId, menuId)
      .then(response => {
        if (!response.success) return
        // Обновляем только если пользователь не переключился на другой продукт
        if (selectedProduct.value?.id === product.id) {
          selectedProductAvailability.value = response.data
        }
      })
      .catch(() => {
        // Тихо игнорируем — модификаторы покажут данные из меню
      })
  }

  const scrollQrFullDialogContentToTop = () => {
    const run = () => {
      const el = document.querySelector('.qr-full-dialog .p-dialog-content') as HTMLElement | null
      if (el) el.scrollTop = 0
    }
    nextTick(() => {
      run()
      requestAnimationFrame(run)
      requestAnimationFrame(() => requestAnimationFrame(run))
    })
  }

  return {
    headerData,
    menuList,
    loading,
    orderLoading,
    themeResponse,
    selectedProduct,
    selectedProductAvailability,
    setSelectedProduct,
    setSelectedProductAvailability,
    openProductDetail,
    scrollQrFullDialogContentToTop,
    productDetailVisible,
    searchQuery,
    menuInitialized,

    flatProductList,
    searchedProducts,
    parentFolders,
    noChildFolders,
    otherFolders,
    childCategories,
    selectedParentId,
    selectedCategoryId,
    selectedCategory,
    stories,
    menuLang,
    getQrMenu,
    menuLoadError,
    clearMenuLoadError,
    themeVars,
    cart,
    addToCart,
    removeFromCart,
    updateSelectedModifiers,
    selectedModifiersByProduct,
    cartTotalPrice,
    cartOrderType,
    cartOrderComment,
    cartProductsForOrder,
    resetCart,
    updateCartProductCount,
    isCartVisible,
    createOrderPayload,
    createTableOrderPayload,
    clientMenuCheckoutFlow,
    currentTableNumber,
    getCheckDetails,
    checkDetails,
    orderFlow,
    fastOrderPay,
    clearOrderScreenError,
    clearOrderScreenContext,
    navigateToMenuFromOrderScreen,
    redirectToOrderScreen,
    finishPaymentReviewAndGoToOrder,
    clearCartAfterSuccessfulOrderPayment,
    getCreatedOrder,
    availableLocales,
    cartItemsCount,
    setPaymentSystem: paymentStore.setPaymentSystem,
    getPaymentSystems: paymentStore.fetchPaymentSystems,
    fetchPaymentSystems: paymentStore.fetchPaymentSystems,
    paymentSystem,
    paymentSystemList,
    resetPaymentForm: paymentStore.resetPaymentForm,
    closeOrderPaymentOtpModal,
    submitOrderPaymentOtp,
    resendOrderPaymentOtp,
    restaurantAvailable,
    restaurantStatusLoading,
    checkRestaurantAvailability,
  }
})
